import { useOnboardingForm } from "@/features/onboarding/hooks/use-onboarding-form";
import { parseLanguage } from "@/i18n/i18n";
import { showAlertCallout } from "@/shared/alerts";
import { getGetMeQueryKey } from "@/spz-api/user/user";
import { Avatar, AvatarFallback, AvatarImage } from "@spz/ui/components/avatar";
import { Button } from "@spz/ui/components/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
} from "@spz/ui/components/form";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@spz/ui/components/select";
import { TextField } from "@spz/ui/components/textfield";
import { Toggle } from "@spz/ui/components/toggle";
import { Typography } from "@spz/ui/components/typography";
import type { ValidationError } from "@spz/ui/components/upload-button";
import { cn } from "@spz/ui/lib/utils";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

interface OnboardingProps {
  className?: string;
  onSuccess?: () => void;
}

export const OnboardingForm = ({
  className,
  onSuccess: onOnboardingSuccess = () => {},
}: OnboardingProps) => {
  const queryClient = useQueryClient();
  const { t, i18n } = useTranslation();
  const [preview, setPreview] = useState<string | null>(null);

  const { form, onSubmit } = useOnboardingForm({
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() });

      const i18nLanguage = parseLanguage(form.getValues("preferredLanguage"));
      if (i18nLanguage !== i18n.language) {
        await i18n.changeLanguage(i18nLanguage);
      }
      try {
        onOnboardingSuccess();
      } catch (error) {
        console.error("Error in success callback:", error);
        showAlertCallout({
          type: "critical",
          title: t("profile_update_error"),
          description: error instanceof Error ? error.message : undefined,
        });
      }
    },
  });
  const handleAvatarUpload = (file: File) => {
    form.setValue("file", file);

    const objectUrl = URL.createObjectURL(file);
    setPreview(objectUrl);
    // Cleanup old URL to avoid memory leaks
    return () => URL.revokeObjectURL(objectUrl);
  };

  const handleAvatarValidationError = (error: ValidationError) => {
    showAlertCallout({
      type: "critical",
      title: error.title,
      description: error.description,
    });
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className={cn(
          "flex flex-col justify-center ",
          "px-6 lg:px-8 xl:px-12 py-auto lg:ml-6 ml-0",
          "max-w-2xl mx-auto",
          className,
        )}
      >
        {/* Header Section */}
        <div className="space-y-8 mb-6">
          <Typography variant="h3" className="text-gray-900">
            {t("my_profile")}
          </Typography>

          <div className="space-y-6">
            <FormField
              control={form.control}
              name="file"
              render={() => (
                <Avatar
                  showEditButton
                  onEdit={handleAvatarUpload}
                  onValidationError={handleAvatarValidationError}
                >
                  {preview && <AvatarImage src={preview} />}
                  <AvatarFallback
                    firstName={form.watch("firstName")}
                    lastName={form.watch("lastName")}
                  />
                </Avatar>
              )}
            />
          </div>
        </div>

        {/* Form Fields Section */}
        <div className="space-y-3">
          <FormField
            control={form.control}
            name="email"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormControl>
                  <TextField
                    {...field}
                    floatingPlaceholder
                    placeholder={t("email_placeholder")}
                    error={fieldState.error}
                    autoComplete="email"
                    disabled
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="firstName"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormControl>
                  <TextField
                    {...field}
                    floatingPlaceholder
                    placeholder={t("first_name_placeholder")}
                    error={fieldState.error}
                    autoComplete="given-name"
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="lastName"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormControl>
                  <TextField
                    {...field}
                    floatingPlaceholder
                    placeholder={t("last_name_placeholder")}
                    error={fieldState.error}
                    autoComplete="family-name"
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="preferredLanguage"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormControl>
                  <Select
                    value={field.value}
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <SelectTrigger
                      placeholder={t("language_placeholder")}
                      floatingPlaceholder
                      error={fieldState.error}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectItem value="fr">{t("french")}</SelectItem>
                        <SelectItem value="en">{t("english")}</SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="notificationsEnabled"
          render={({ field }) => (
            <div className="flex items-center justify-between mt-6">
              <Typography variant="caption" className="text-gray-800">
                {t("email_notifications")}
              </Typography>
              <Toggle
                checked={field.value}
                onCheckedChange={field.onChange}
                name={field.name}
                ref={field.ref}
                onBlur={field.onBlur}
              />
            </div>
          )}
        />
        {/* Footer Section */}
        <div className="pt-6">
          <Button type="submit" className="w-full">
            {t("validate")}
          </Button>
        </div>
      </form>
    </Form>
  );
};
